import { Track } from 'livekit-client'
import type { AudioProcessorOptions, TrackProcessor } from 'livekit-client'
import { RnnoiseWorkletNode, NoiseGateWorkletNode, loadRnnoise } from '@sapphi-red/web-noise-suppressor'
import * as Sentry from '@sentry/nuxt'

export class AggressiveNoiseProcessor implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
    readonly name = 'AggressiveNoiseProcessor'

    private static workletLoaders = new WeakMap<AudioContext, Promise<void>>()
    private static rnnoiseWasmLoader: Promise<ArrayBuffer> | null = null

    processedTrack?: MediaStreamTrack

    private sourceNode: MediaStreamAudioSourceNode | null = null
    private rnnoiseNode: RnnoiseWorkletNode | null = null
    private noiseGateNode: NoiseGateWorkletNode | null = null
    private gainNode: GainNode | null = null
    private destinationNode: MediaStreamAudioDestinationNode | null = null
    private audioContext: AudioContext | null = null
    private ownsAudioContext = false
    private volume: number
    private readonly externalAudioContext: AudioContext | null

    constructor(options: {volume?: number, audioContext?: AudioContext} = {}) {
        this.volume = this.normalizeVolume(options.volume ?? 1)
        this.externalAudioContext = options.audioContext ?? null
    }

    async init(options: AudioProcessorOptions): Promise<void> {
        const ctx = this.getAudioContext(options)

        if (ctx.state === 'suspended') await ctx.resume()

        try {
            const [rnnoiseWasm] = await Promise.all([
                AggressiveNoiseProcessor.loadRnnoiseWasm(),
                AggressiveNoiseProcessor.loadWorklets(ctx),
            ])

            this.sourceNode = ctx.createMediaStreamSource(new MediaStream([options.track]))
            this.destinationNode = ctx.createMediaStreamDestination()
            this.gainNode = ctx.createGain()
            this.gainNode.gain.value = this.volume
            // The RNNoise/gate worklets (`maxChannels: 1`) still output stereo with only the left
            // channel filled — take just that one, so the processed track is mono and plays in both
            // ears (self-monitoring) instead of one, and isn't halved by a stereo->mono downmix.
            this.gainNode.channelCount = 1
            this.gainNode.channelCountMode = 'explicit'
            this.gainNode.channelInterpretation = 'discrete'

            this.noiseGateNode = new NoiseGateWorkletNode(ctx, {
                openThreshold: -62,
                closeThreshold: -72,
                holdMs: 180,
                maxChannels: 1,
            })

            this.rnnoiseNode = new RnnoiseWorkletNode(ctx, {
                wasmBinary: rnnoiseWasm,
                maxChannels: 1,
            })

            this.sourceNode.connect(this.rnnoiseNode)
            this.rnnoiseNode.connect(this.noiseGateNode)
            this.noiseGateNode.connect(this.gainNode)
            this.gainNode.connect(this.destinationNode)

            const [processedTrack] = this.destinationNode.stream.getAudioTracks()
            if (!processedTrack) {
                throw new Error('Processed audio track was not created')
            }

            this.processedTrack = processedTrack

            console.log('[AggressiveNoiseProcessor] RNNoise + hard noise gate loaded', {
                ownsAudioContext: this.ownsAudioContext,
                processedTrackId: processedTrack.id,
                processedTrackState: processedTrack.readyState,
            })
        } catch (e) {
            await this.destroy()
            console.error('[AggressiveNoiseProcessor] Failed:', e)
            Sentry.captureException(e, { extra: { action: 'init_noise_processor' } })
            throw e
        }
    }

    setVolume(volume: number) {
        this.volume = this.normalizeVolume(volume)
        if (!this.gainNode) return

        this.gainNode.gain.setTargetAtTime(this.volume, this.gainNode.context.currentTime, 0.01)
    }

    async restart(options: AudioProcessorOptions): Promise<void> {
        await this.destroy()
        await this.init(options)
    }

    async destroy(): Promise<void> {
        this.sourceNode?.disconnect()
        this.rnnoiseNode?.disconnect()
        this.noiseGateNode?.disconnect?.()
        this.gainNode?.disconnect()
        this.rnnoiseNode?.destroy?.()
        this.processedTrack?.stop()
        if (this.ownsAudioContext && this.audioContext?.state !== 'closed') {
            await this.audioContext.close()
        }
        this.sourceNode = null
        this.noiseGateNode = null
        this.rnnoiseNode = null
        this.gainNode = null
        this.destinationNode = null
        this.audioContext = null
        this.ownsAudioContext = false
        this.processedTrack = undefined
    }

    private normalizeVolume(volume: number) {
        if (!Number.isFinite(volume)) return 1
        return Math.min(1, Math.max(0, volume))
    }

    private getAudioContext(options: AudioProcessorOptions) {
        // Prefer a caller-supplied, long-lived context so the worklet modules
        // (cached in `loadWorklets` per-AudioContext) and RNNoise WASM stay
        // warm across reconnects instead of being reloaded on every call join.
        const sharedContext = this.externalAudioContext ?? options.audioContext
        if (sharedContext) {
            this.audioContext = sharedContext
            this.ownsAudioContext = false
            return sharedContext
        }

        const AudioContextClass = globalThis.AudioContext || (globalThis as any).webkitAudioContext
        if (!AudioContextClass) {
            throw new Error('AudioContext is not available')
        }

        this.audioContext = new AudioContextClass({latencyHint: 'interactive'})
        this.ownsAudioContext = true
        return this.audioContext
    }

    private static loadRnnoiseWasm() {
        if (!this.rnnoiseWasmLoader) {
            this.rnnoiseWasmLoader = loadRnnoise({
                url: new URL('@sapphi-red/web-noise-suppressor/rnnoise.wasm', import.meta.url).href,
                simdUrl: new URL('@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm', import.meta.url).href,
            }).catch((error) => {
                this.rnnoiseWasmLoader = null
                throw error
            })
        }

        return this.rnnoiseWasmLoader
    }

    private static loadWorklets(ctx: AudioContext) {
        let loader = this.workletLoaders.get(ctx)
        if (!loader) {
            loader = Promise.all([
                ctx.audioWorklet.addModule(
                    new URL('@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js', import.meta.url).href
                ),
                ctx.audioWorklet.addModule(
                    new URL('@sapphi-red/web-noise-suppressor/noiseGateWorklet.js', import.meta.url).href
                ),
            ]).then(() => undefined).catch((error) => {
                this.workletLoaders.delete(ctx)
                throw error
            })
            this.workletLoaders.set(ctx, loader)
        }

        return loader
    }
}
